// Separate admin-only login page. Lives at /admin/login.
// No Google OAuth, no register link, no public branding clutter.
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Shield, Loader2, Leaf } from "lucide-react";
import { toast } from "sonner";

export default function AdminLogin() {
  const { login, logout } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        await logout();
        setError("This account doesn't have admin access.");
        toast.error("Not an admin account");
        return;
      }
      toast.success("Welcome back, admin.");
      nav("/admin", { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-ink relative overflow-hidden" data-testid="admin-login-page">
      {/* atmospheric layers */}
      <div className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full bg-moss/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-terra/15 blur-3xl pointer-events-none" />
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />

      <div className="relative w-full max-w-md px-6">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm" data-testid="admin-login-home">
            <Leaf size={14} /> Danashakaa.com
          </Link>
        </div>

        <form onSubmit={submit}
          data-testid="admin-login-form"
          className="bg-white rounded-3xl shadow-hover p-8 space-y-5 border border-subtle relative">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-2xl bg-moss text-white grid place-items-center">
              <Shield size={20} />
            </span>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-ink leading-tight">Admin sign in</h1>
              <p className="text-xs text-muted2">Restricted area · authorised staff only</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink mb-2">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
              autoComplete="username"
              className="w-full rounded-xl border border-subtle bg-white px-4 py-3 text-ink focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20 transition-all" />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink mb-2">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              autoComplete="current-password"
              className="w-full rounded-xl border border-subtle bg-white px-4 py-3 text-ink focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20 transition-all" />
          </div>

          {error && <p className="text-sm text-danger" data-testid="admin-login-error">{error}</p>}

          <button type="submit" disabled={busy} data-testid="admin-login-submit"
            className="w-full inline-flex justify-center items-center gap-2 bg-ink hover:bg-black text-white rounded-full px-6 py-3 font-semibold transition-all active:scale-95 disabled:opacity-60">
            {busy && <Loader2 className="animate-spin" size={18} />} Sign in to admin
          </button>

          <p className="text-xs text-soft text-center">
            Not staff? <Link to="/login" className="text-moss font-medium hover:underline">Member login</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
